import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Folder, FolderPlus, Home, Pencil, Trash2, X, Check } from 'lucide-react';

/** A single droppable folder row. */
const FolderRow = ({ folder, active, onSelect, onRename, onDelete }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `folder:${folder._id}`,
    data: { type: 'folder', folderId: folder._id },
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(folder.name || '');
  const [menuOpen, setMenuOpen] = useState(false);

  const commitRename = () => {
    const name = draft.trim();
    setEditing(false);
    if (name && name !== folder.name) onRename(folder._id, name);
    else setDraft(folder.name || '');
  };

  return (
    <div
      ref={setNodeRef}
      className={[
        'mv2-folder-row',
        active && 'mv2-folder-row--active',
        isOver && 'mv2-folder-row--over',
      ].filter(Boolean).join(' ')}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenuOpen(true);
      }}
    >
      <button
        type="button"
        className="mv2-folder-row__main"
        onClick={() => onSelect(folder._id)}
      >
        <Folder size={14} />
        {editing ? (
          <input
            className="mv2-folder-row__input"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') { setEditing(false); setDraft(folder.name || ''); }
            }}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="mv2-folder-row__name" title={folder.name}>{folder.name}</span>
        )}
      </button>
      {!editing && (
        <button
          type="button"
          className="mv2-folder-row__more"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          aria-label="Folder actions"
        >
          <Pencil size={12} />
        </button>
      )}
      {menuOpen && (
        <div
          className="mv2-folder-menu"
          onMouseLeave={() => setMenuOpen(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" onClick={() => { setMenuOpen(false); setEditing(true); }}>
            <Pencil size={12} /> Rename
          </button>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onDelete(folder); }}
            className="mv2-folder-menu__danger"
          >
            <Trash2 size={12} /> Delete…
          </button>
        </div>
      )}
    </div>
  );
};

const RootRow = ({ active, onSelect }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'folder:root',
    data: { type: 'folder', folderId: null },
  });
  return (
    <div
      ref={setNodeRef}
      className={[
        'mv2-folder-row',
        active && 'mv2-folder-row--active',
        isOver && 'mv2-folder-row--over',
      ].filter(Boolean).join(' ')}
    >
      <button type="button" className="mv2-folder-row__main" onClick={() => onSelect(null)}>
        <Home size={14} />
        <span className="mv2-folder-row__name">All media</span>
      </button>
    </div>
  );
};

const FolderSidebar = ({
  folders = [],
  activeFolderId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}) => {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');

  const cancelCreate = () => { setCreating(false); setDraft(''); };

  const submit = () => {
    const name = draft.trim();
    cancelCreate();
    if (name) onCreate(name);
  };

  const handleSelect = (id) => {
    cancelCreate();
    if (onSelect) onSelect(id);
  };

  return (
    <aside className="mv2-folder-sidebar" aria-label="Folders">
      <div className="mv2-folder-sidebar__header">
        <span className="mv2-folder-sidebar__title">Folders</span>
      </div>

      <div className="mv2-folder-sidebar__list">
        <RootRow active={!activeFolderId} onSelect={handleSelect} />

        {folders.map((f) => (
          <FolderRow
            key={f._id}
            folder={f}
            active={activeFolderId === f._id}
            onSelect={handleSelect}
            onRename={onRename}
            onDelete={onDelete}
          />
        ))}

        {!creating && (
          <button
            type="button"
            className="mv2-folder-sidebar__new"
            onClick={() => setCreating(true)}
          >
            <FolderPlus size={14} /> New folder
          </button>
        )}

        {creating && (
          <div className="mv2-folder-row mv2-folder-row--creating">
            <Folder size={14} />
            <input
              autoFocus
              className="mv2-folder-row__input"
              placeholder="Folder name"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                else if (e.key === 'Escape') { setCreating(false); setDraft(''); }
              }}
            />
            <button
              type="button"
              className="mv2-folder-row__iconbtn mv2-folder-row__iconbtn--confirm"
              onClick={submit}
              aria-label="Confirm"
            ><Check size={14} /></button>
            <button
              type="button"
              className="mv2-folder-row__iconbtn"
              onClick={cancelCreate}
              aria-label="Cancel"
            ><X size={14} /></button>
          </div>
        )}

        {!creating && folders.length === 0 && (
          <p className="mv2-folder-sidebar__empty">
            No folders yet. Create one to group assets.
          </p>
        )}
      </div>
    </aside>
  );
};

export default FolderSidebar;
